import { ImageInfo, InventoryAttributeSelector, ProductAttribute, ProductCardImage } from "../types/common"
import InventoryModel from "./inventory"

export interface ProductPromotion {
  id: number
  type: string
  type_label: string
  name: string
  label: string
}

/**
 * 2026-10-12 presale: flash_sales[] items now carry a stable `status` key
 * mapped from the backend Promotion::getStatusKeyAttribute().
 *  - `upcoming` (PromotionStatusType::COMMING)  -> sap co gia sale
 *  - `active`   (PromotionStatusType::HAPPENDING) -> dang sale
 *  - `ended`    (PromotionStatusType::STOPPED)   -> da ket thuc
 *  - `paused`   (PromotionStatusType::PAUSE)     -> tam dung
 *
 * The `from` / `to` fields are ISO-8601 strings (toIso8601String()).
 * Storefront code may derive a `from > now()` check from these, but
 * the canonical source of truth is `status`.
 */
export interface FlashSale {
  id: number
  name: string
  from: string | null
  to: string | null
  status: 'upcoming' | 'active' | 'ended' | 'paused'
  discount_percent: number
  badge_label: string
}

export default class ProductModel {
  id: number
  name: string
  lazy_image: string
  // 2026-10-12 presale: typed FlashSale[]. `status` is the canonical
  // source of truth for which UI to render (panel vs banner vs nothing).
  flash_sales: FlashSale[]
  promotions: ProductPromotion[]
  promotion_price: number | null
  sale_price: number
  liked: boolean
  discount_percent: number
  is_new: boolean
  slug: string
  description: string
  spec_attributes?: { label: string; value: string; visible?: boolean }[]
  is_reorder: boolean
  condition: string
  sku: string | null
  short_description: string
  additional_information: string
  shipping_and_return: string
  code: string
  images: ProductCardImage[]
  inventories: InventoryModel[]
  size_image: string
  size_images?: string[]
  attributes: ProductAttribute[]
  out_of_stock: boolean
  meta: string
  is_best_seller: boolean
  image: ImageInfo
  original_price: number
  video?: string

  get hasPromotion() {
    return this.promotion_price > 0
  }

  getInventoriesByFirstAttribute(value: string): InventoryModel[] {
    return this.inventories.filter(inventory => {
      return inventory.firstAttribute?.value === value
    })
  }

  getInventoriesByAttributes(values: string[]): InventoryModel[] {
    return this.inventories.filter(inventory => {
      const attributes = inventory.attributes?.filter(attribute =>
        values.includes(attribute.value)
      )
      return attributes.length === values.length
    })
  }

  get firstAttributeOptions(): InventoryAttributeSelector {
    const uniqueAttributes = [
      ...new Map((this.attributes || []).map(attribute => [attribute.value, attribute])).values()
    ]

    if (!uniqueAttributes.length) {
      return { options: [], children: [] }
    }

    return {
      name: uniqueAttributes[0].name,
      id: uniqueAttributes[0].id,
      options: uniqueAttributes.map(attribute => ({
        label: attribute.value,
        value: attribute.value,
        image: attribute.image,
        small_image: attribute.small_image
      })),
      children: uniqueAttributes[0].children
    }
  }

  get secondAttributeOptions(): InventoryAttributeSelector {
    if (!this.firstAttributeOptions?.children?.length) return null
    const attribute = this.firstAttributeOptions.children[0]
    return {
      name: attribute.name,
      id: attribute.id,
      options: this.firstAttributeOptions.children.map(child => {
        return {
          label: child.value,
          value: child.value,
          image: this.inventories.find(
            inventory =>
              inventory.attributes.filter(a =>
                [child.value, attribute.value].includes(a.value)
              ).length == 2
          )?.image
        }
      }),
      children: []
    }
  }
  static fromJson(json) {
    if (!json) return null
    if (json instanceof ProductModel) return json
    const product = new ProductModel()
    for (const key in json) {
      if (Object.prototype.hasOwnProperty.call(json, key)) {
        product[key] = json[key]
      }
    }
    if (Array.isArray(json?.inventories)) {
      product.inventories = json.inventories.map(inventory =>
        inventory instanceof InventoryModel
          ? inventory
          : InventoryModel.fromJson(inventory)
      )
    } else {
      product.inventories = []
    }
    return product
  }
}
